import type { VariantQuantityBreak } from 'services/product.service';

// Product related types
export interface Product {
  id: string;
  name: string;
  description: string;
  price: number; // Price excluding VAT
  taxRate?: number; // VAT rate as percentage (e.g., 20 for 20%)
  originalPrice?: number;
  currency: string;
  category: string;
  subcategory?: string;
  images: string[];
  specifications: ProductSpecification[];
  code: string;
  inStock: boolean;
  stockQuantity: number;
  requiresLogin: boolean;
  tags: string[];
  rating?: number;
  reviewCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProductSpecification {
  name: string;
  value: string;
}

export interface Category {
  id: string;
  name: string;
  description: string;
  image: string;
  slug: string;
  parentId?: string;
  children?: Category[];
  productCount: number;
}

// User related types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  phone?: string;
  address?: Address;
  role: UserRole;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  accountCode?: string; // Customer account code (cd_ow_account) for order webhooks
  accountCustomer?: boolean; // Whether the customer trades on account (cd_account_customer)
  hasAccountCredit?: boolean; // Whether user has credit account for paying on account
  creditLimit?: number; // Customer's credit limit
  availableToSpend?: number; // OrderWise available to spend (ccdt_available_to_spend)
  creditCurrencyCode?: string; // Currency of the credit figures (not always GBP)
  creditCurrencySymbol?: string;
  balance?: number; // Customer account balance
  onHold?: boolean; // Whether customer account is on hold
  // Statement/Delivery address fields from backend customer data
  address1?: string;
  address2?: string;
  address3?: string;
  town?: string;
  county?: string;
  postcode?: string;
  country?: string;
}

export interface Address {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export enum UserRole {
  CUSTOMER = 'customer',
  ADMIN = 'admin',
  MANAGER = 'manager',
}

// Cart and order types
export interface CartItem {
  id: string; // Unique identifier for this cart item (includes variant info)
  productId: string;
  quantity: number;
  selectedMould?: string;
  selectedShade?: string;
  // Product data stored with cart item for display
  productName: string;
  productCode: string;
  price: number; // Price excluding VAT
  originalPrice?: number;
  originalPriceIncVat?: number;
  discountPercent?: number;
  description?: string; // Variant description (from variant.variantDescription)
  priceIncVat?: number; // Price including VAT (from backend rspIncVat)
  taxRate?: number; // VAT rate as percentage (e.g., 20 for 20%)
  taxRateId?: number; // Tax rate ID from vat_rate table (e.g., 2 for T1/20%)
  currency: string;
  images: string[];
  category?: string;
  /** Weight per unit in kg (from variant measurements) - used for delivery pricing */
  weight?: number;
  /** OrderWise quantity break tiers (Special Offers / bulk pricing) */
  quantityBreaks?: VariantQuantityBreak[];
  /** Unit price before quantity-break discount (ex VAT) */
  basePrice?: number;
  basePriceIncVat?: number;
}

export interface Cart {
  items: CartItem[];
  total: number;
  itemCount: number;
}

export interface Order {
  id: string;
  userId: string;
  items: OrderItem[];
  status: OrderStatus;
  total: number;
  shippingAddress: Address;
  billingAddress: Address;
  paymentMethod: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrderItem {
  productId: string;
  quantity: number;
  price: number; // Price excluding VAT
  taxRate?: number; // VAT rate as percentage (e.g., 20 for 20%)
  product: Product;
}

export enum OrderStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  SHIPPED = 'shipped',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
}

// Navigation types
export interface NavigationItem {
  label: string;
  path: string;
  children?: NavigationItem[];
}

// SEO types
export interface SEOData {
  title: string;
  description: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
}

// API response types
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
  errors?: string[];
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Form types
export interface LoginForm {
  email: string;
  password: string;
}

export interface RegisterForm {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  company: string;
  phone?: string;
}

export interface ContactForm {
  name: string;
  email: string;
  company: string;
  phone?: string;
  message: string;
}

// Filter and search types
export interface ProductFilters {
  category?: string;
  priceRange?: {
    min: number;
    max: number;
  };
  inStock?: boolean;
  rating?: number;
  tags?: string[];
}

export interface SearchParams {
  query: string;
  filters: ProductFilters;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page: number;
  limit: number;
}

// Testimonial types
export interface Testimonial {
  id: string;
  name: string;
  title: string;
  company: string;
  location: string;
  rating: number;
  content: string;
  avatar?: string;
  createdAt: string;
}

// Partner/Logo types
export interface Partner {
  id: string;
  name: string;
  logo: string;
  website?: string;
  description?: string;
}

// Newsletter types
export interface NewsletterSubscription {
  email: string;
  preferences?: {
    productUpdates: boolean;
    specialOffers: boolean;
    industryNews: boolean;
  };
}

// Checkout types
export interface DeliveryAddress {
  id: string;
  firstName: string;
  lastName: string;
  reference: string;
  companyName: string;
  address1: string;
  address2?: string;
  address3?: string;
  city: string;
  town?: string; // Alternative to city (some addresses use town instead)
  county: string;
  country: string;
  postcode: string;
  phoneNumber?: string;
  isDefault?: boolean;
  label?: string; // e.g., "Main Office", "Home"
}

export interface DeliveryMethod {
  id: string;
  name: string;
  description: string;
  price: number; // Price excluding VAT
  taxRate?: number; // VAT rate as percentage (e.g., 20 for 20%)
  estimatedDays?: string;
  isAvailable?: boolean;
  // New fields for delivery provider integration
  deliveryProvider?: 'DHL' | 'DPD' | 'Royal Mail';
  royalMailServiceCode?: 'OLP2' | 'SD1OLP';
  useDhlDelivery?: boolean;
  useDpdDelivery?: boolean;
  deliveryServiceTier?: 'standard' | 'pre12' | 'pre1030';
  deliveryMethodId?: number;
  zone?: string;
  parcelCount?: number;
  ratePerParcel?: number;
  /** When true, importer EORI and VAT are required for DPD shipments */
  requiresCustomsData?: boolean;
  importerEoriNumber?: string;
  importerVatNumber?: string;
}

export interface PaymentMethod {
  id: string;
  name: string;
  description: string;
  icon: string;
  isAvailable: boolean;
}

export interface CheckoutStep {
  id: number;
  title: string;
  isCompleted: boolean;
  isActive: boolean;
  isLocked: boolean;
}

export interface CheckoutState {
  currentStep: number;
  steps: CheckoutStep[];
  selectedAddress?: DeliveryAddress;
  selectedDeliveryMethod?: DeliveryMethod;
  selectedPaymentMethod?: PaymentMethod;
  newAddress?: Partial<DeliveryAddress>;
  isAddingNewAddress: boolean;
}

export interface OrderSummary {
  items: OrderItem[];
  subtotal: number;
  vat: number;
  total: number;
  currency: string;
} 

// Order data structure used in checkout (different from OrderSummary)
export interface CheckoutOrderData {
  items: Array<{
    id: string;
    name: string;
    sku: string;
    mould?: string;
    shade?: string;
    price: number;
    quantity: number;
    total: number;
    image?: string;
  }>;
  subtotal: number;
  delivery: number;
  epCharge?: number; // EP Environmental Packaging gross amount (if active)
  /** Discount off the goods, ex VAT, as priced by the server (0 when none). */
  discountNet?: number;
  /** The code that discount came from, for display. */
  discountCode?: string;
  totalNet: number; // Items + delivery + EP ex VAT, less any discount
  vat: number;
  total: number;
}
// Order list and detail types (from OrderWise API)
export interface OrderListItem {
  orderId: number;
  orderNumber: string;
  ecommerceOrderNumber: string | null;
  customerOrderReference: string;
  orderDate: string;
  requiredDate: string;
  promisedDate: string;
  statusId: number;
  statusName: string | null;
  onHold: boolean;
  orderComplete: boolean;
  totalGross: number;
  totalNet: number;
  totalVat: number;
  invoiceCount: number;
  shipmentCount: number;
  currencyId?: number;
  currencyCode?: 'GBP' | 'EUR';
}

export interface OrderDetail {
  orderId: number;
  orderNumber: string;
  ecommerceOrderNumber: string | null;
  customerOrderReference: string;
  orderDate: string;
  requiredDate: string;
  promisedDate: string;
  paymentMethod?: 'account-credit' | 'worldpay' | null;
  paymentMethodLabel?: string | null;
  statusId: number;
  onHold: boolean;
  currencyId?: number;
  currencyCode?: 'GBP' | 'EUR';
  lineItems: Array<{
    id: number;
    lineNumber: number;
    quantity: number;
    quantitySent: number;
    description: string;
    variant: {
      id: number;
      code: string;
      description: string;
    } | null;
    pricing: {
      itemNet: number;
      itemVat: number;
      itemGross: number;
      lineNet: number;
      lineVat: number;
      lineGross: number;
    };
  }>;
  discountsSurcharges?: Array<{
    id: number;
    description: string;
    net: number;
    vat: number;
    gross: number;
    isDiscount: boolean;
    type: 'discount' | 'surcharge';
  }>;
  totals: {
    goodsNet: number;
    goodsVat: number;
    goodsGross: number;
    deliveryNet: number;
    deliveryVat: number;
    deliveryGross: number;
    totalNet: number;
    totalVat: number;
    totalGross: number;
  };
  deliveryAddress?: {
    name: string;
    address1: string;
    address2?: string;
    address3?: string;
    town: string;
    county?: string;
    country?: string;
    postcode: string;
  };
  shipmentInfo?: {
    trackingNumber?: string;
    trackingUrl?: string;
    courier?: string;
  };
}
